import React, { useEffect, useState, useContext, useCallback } from "react";
import loginBanner from "../../images/loginBanner.svg";
import sidebarLogo from "../../images/sidebarLogo.svg";
import { KBContext, AutherContext, ToastContext } from "../../Context";
import { getLogo, initiateLoginHOT } from "./utils.js";
import { getEmailProductsHOT, passwordSignInHOT, validateTokenHOT } from "../../Service/LoginServices";
import { validateEmail } from "../../Constants/utility.js";
import useNavigateWithSearch from "../../Context/KBContext/hooks/useNavigateWithSearch.js";
import { buildSettings } from "../../Constants/Settings";
import {
    LOGO_IMG,
    AUTH_PROVIDER,
    welcomeTitle,
    welcomeSubtitle,
    workEmailLabel,
    emailInputPlaceholder,
    continueButtonText,
    signInWithGoogleText,
    signInWithMicrosoftText,
    enterPasswordLabel,
    enterPasswordPlaceholder,
    logInButtonText,
} from "./constants";
import {
    PageWrapper,
    ImagePanel,
    RightPanel,
    FormContainer,
    WizardTitle,
    WizardSubtitle,
    TitleGroup,
    InputLabel,
    WizardInput,
    InputGroup,
    PrimaryButton,
    WizardErrorMessage,
    BackHeaderWrapper,
    BackArrowButton,
    EmailPill,
    EmailDot,
    WizardLogoWrapper,
    OAuthButton,
    LogoWrapper,
    LogoImage,
} from "./styled.js";

// ── Steps ──
const STEPS = { EMAIL: 'email', SIGN_IN: 'signIn' };

// ── Sign-in modes derived from product + provider detection ──
const SIGN_IN_MODE = {
    HIG_GOOGLE: 'hig_google',       // HIG only → Google sign-in
    OMNI_FULL: 'omni_full',         // Omni → Google + Microsoft + Password
};

// ── SVG Icons ──

const BackArrow = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 12H5M12 19l-7-7 7-7" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4"/>
        <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853"/>
        <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#FBBC05"/>
        <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z" fill="#EA4335"/>
    </svg>
);

const MicrosoftIcon = () => (
    <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
        <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
        <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
        <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
        <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
);

const OrDivider = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: '#94a3b8' }}>OR</span>
        <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
    </div>
);

export default function NewLoginPage() {
    const { setShowToast } = useContext(ToastContext);
    const { data: kbData } = useContext(KBContext);
    const {
        setRefetchToken,
        setRedirectPage,
        setEmail: setContextEmail,
        hasPermission,
        setLoginInitiated,
        initiateLogin,
        initiateAuthWithUserDetails,
    } = useContext(AutherContext);
    const navigate = useNavigateWithSearch();

    const [currentStep, setCurrentStep] = useState(STEPS.EMAIL);
    const [email, setEmail] = useState('');
    const [signInMode, setSignInMode] = useState(null);
    const [mfaEnabled, setMfaEnabled] = useState(false);
    const [password, setPassword] = useState('');
    const [otp, setOtp] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // Redirect when authenticated
    useEffect(() => {
        const { isInternal } = kbData || {};
        if (isInternal !== undefined) {
            if ((isInternal && hasPermission) || !isInternal) {
                setLoginInitiated(false);
                navigate("/");
            }
        }
    }, [kbData, hasPermission, setLoginInitiated, navigate]);

    // ── HIG/Google Login ──
    const triggerGoogleLogin = useCallback(() => {
        if (buildSettings.NEW_AUTHER && initiateLogin) {
            initiateLogin({ email });
        } else {
            setRefetchToken(true);
            if (setRedirectPage) setRedirectPage(true);
            if (setContextEmail) setContextEmail(email);
        }
    }, [email, initiateLogin, setRefetchToken, setRedirectPage, setContextEmail]);

    // ── Access token extraction ──
    const getAccessToken = (response) => {
        const headers = response?.headers || {};
        const token = headers["access-token"] ?? headers["Access-Token"];
        if (token) return token;
        const auth = headers.Authorization ?? headers.authorization;
        if (auth && /^Bearer\s+/i.test(auth)) return auth.replace(/^Bearer\s+/i, "").trim();
        return null;
    };

    // ── Step 1: Email Continue ──
    const handleEmailContinue = useCallback(async () => {
        if (!validateEmail(email)) {
            setError('Please enter a valid email address.');
            return;
        }
        setError('');
        setIsLoading(true);
        try {
            const productsRes = await getEmailProductsHOT(email);
            const productsData = productsRes?.data || {};
            const isRegisteredOnOmni = productsData.is_registered_on_omni === true;

            if (!isRegisteredOnOmni) {
                // HIG only — show Google sign-in
                setSignInMode(SIGN_IN_MODE.HIG_GOOGLE);
                setCurrentStep(STEPS.SIGN_IN);
            } else {
                // Omni user — show full sign-in options
                const mfaEnabled = productsData.mfa_enabled === true || productsData.mfaEnabled === true;
                setMfaEnabled(mfaEnabled);
                setSignInMode(SIGN_IN_MODE.OMNI_FULL);
                setCurrentStep(STEPS.SIGN_IN);
            }
        } catch (err) {
            const message = err?.response?.data?.message || err?.response?.data?.errors?.[0] || err?.message || 'Something went wrong. Please try again.';
            setShowToast(message);
        } finally {
            setIsLoading(false);
        }
    }, [email, setShowToast]);

    // ── Password Login ──
    const handlePasswordLogin = useCallback(async () => {
        if (!password.trim()) return;
        setError('');
        setIsLoading(true);
        try {
            const otpAttempt = mfaEnabled && otp ? otp : null;
            const signInResponse = await passwordSignInHOT(email, password, otpAttempt);
            const accessToken = getAccessToken(signInResponse);
            if (accessToken) {
                const validateRes = await validateTokenHOT(accessToken);
                const payloadData = validateRes?.data?.data;
                if (initiateAuthWithUserDetails && payloadData?.id != null && payloadData?.accounts?.[0]?.id != null) {
                    await initiateAuthWithUserDetails({ user_id: payloadData.id, usergroup_id: payloadData.accounts[0].id });
                }
            }
            navigate("/");
        } catch (err) {
            const message = err?.response?.data?.message || err?.response?.data?.errors?.[0] || err?.message || 'Login failed. Please try again.';
            setShowToast(message);
            setError(message);
        } finally {
            setIsLoading(false);
        }
    }, [email, password, otp, mfaEnabled, initiateAuthWithUserDetails, navigate, setShowToast]);

    // ── Back Navigation ──
    const goBack = useCallback(() => {
        setError('');
        setPassword('');
        setOtp('');
        setSignInMode(null);
        setMfaEnabled(false);
        setCurrentStep(STEPS.EMAIL);
    }, []);

    // ── KB Logo ──
    let logo = getLogo(kbData);
    logo = logo ? LOGO_IMG.replaceAll('{{LOGO}}', logo) : false;

    return (
        <PageWrapper>
            <ImagePanel>
                <img alt="Welcome illustration" src={loginBanner} />
            </ImagePanel>
            <RightPanel>
                {/* ── Step 1: Email ── */}
                {currentStep === STEPS.EMAIL && (
                    <FormContainer>
                        {logo ? (
                            <LogoWrapper><LogoImage alt="Logo" src={logo} /></LogoWrapper>
                        ) : (
                            <WizardLogoWrapper>
                                <img alt="Hiver Knowledge Base" src={sidebarLogo} />
                            </WizardLogoWrapper>
                        )}
                        <TitleGroup>
                            <WizardTitle>{welcomeTitle}</WizardTitle>
                            <WizardSubtitle>{welcomeSubtitle}</WizardSubtitle>
                        </TitleGroup>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <InputGroup>
                                <InputLabel htmlFor="login-email">{workEmailLabel}</InputLabel>
                                <WizardInput
                                    id="login-email"
                                    type="email"
                                    placeholder={emailInputPlaceholder}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && validateEmail(email) && !isLoading && handleEmailContinue()}
                                    autoFocus
                                />
                                {error && <WizardErrorMessage>{error}</WizardErrorMessage>}
                            </InputGroup>
                            <PrimaryButton disabled={!validateEmail(email) || isLoading} onClick={handleEmailContinue}>
                                {isLoading ? 'Verifying…' : continueButtonText}
                            </PrimaryButton>
                        </div>
                    </FormContainer>
                )}

                {/* ── Step 2: Sign In ── */}
                {currentStep === STEPS.SIGN_IN && (
                    <FormContainer style={{ width: '400px', maxWidth: '400px' }}>
                        <BackHeaderWrapper>
                            <BackArrowButton onClick={goBack}><BackArrow /></BackArrowButton>
                            <EmailPill><EmailDot />{email}</EmailPill>
                        </BackHeaderWrapper>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                            {/* HIG only → Google sign-in */}
                            {signInMode === SIGN_IN_MODE.HIG_GOOGLE && (
                                <>
                                    <TitleGroup>
                                        <WizardTitle>Sign into Hiver in Gmail</WizardTitle>
                                        <WizardSubtitle>Your account uses Google sign-in. We'll redirect you to Google to authenticate.</WizardSubtitle>
                                    </TitleGroup>
                                    <OAuthButton onClick={triggerGoogleLogin}>
                                        <GoogleIcon />{signInWithGoogleText}
                                    </OAuthButton>
                                </>
                            )}

                            {/* Omni → Google + Microsoft + Password */}
                            {signInMode === SIGN_IN_MODE.OMNI_FULL && (
                                <>
                                    <TitleGroup>
                                        <WizardTitle>Sign into Omni</WizardTitle>
                                        <WizardSubtitle>Choose how you'd like to sign in.</WizardSubtitle>
                                    </TitleGroup>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <OAuthButton onClick={triggerGoogleLogin}>
                                            <GoogleIcon />{signInWithGoogleText}
                                        </OAuthButton>
                                        <OAuthButton onClick={() => initiateLoginHOT({ provider: AUTH_PROVIDER.MICROSOFT, onError: setShowToast })}>
                                            <MicrosoftIcon />{signInWithMicrosoftText}
                                        </OAuthButton>
                                    </div>
                                    <OrDivider />
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        <InputGroup>
                                            <InputLabel htmlFor="login-password">{enterPasswordLabel}</InputLabel>
                                            <WizardInput
                                                id="login-password"
                                                type="password"
                                                placeholder={enterPasswordPlaceholder}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && password.trim() && handlePasswordLogin()}
                                                autoFocus
                                            />
                                        </InputGroup>
                                        {mfaEnabled && (
                                            <InputGroup>
                                                <InputLabel htmlFor="login-otp">Enter verification code</InputLabel>
                                                <WizardInput
                                                    id="login-otp"
                                                    type="text"
                                                    inputMode="numeric"
                                                    autoComplete="one-time-code"
                                                    placeholder="e.g. 234678"
                                                    value={otp}
                                                    onChange={(e) => setOtp((e.target.value || '').replace(/\D/g, '').slice(0, 6))}
                                                    onKeyDown={(e) => e.key === 'Enter' && password.trim() && handlePasswordLogin()}
                                                />
                                            </InputGroup>
                                        )}
                                        {error && <WizardErrorMessage>{error}</WizardErrorMessage>}
                                        <PrimaryButton
                                            disabled={!password.trim() || (mfaEnabled && (!otp.trim() || otp.length < 6)) || isLoading}
                                            onClick={handlePasswordLogin}
                                        >
                                            {isLoading ? 'Signing in…' : logInButtonText}
                                        </PrimaryButton>
                                    </div>
                                </>
                            )}
                        </div>
                    </FormContainer>
                )}
            </RightPanel>
        </PageWrapper>
    );
}
